import { useEffect, useState } from "react";
import { PackageCheck, XCircle } from "lucide-react";
import api from "@/lib/api";
import AdminNav from "@/components/AdminNav";

export default function AdminMemberships() {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api
      .get("/admin/memberships")
      .then((res) => setMemberships(res.data))
      .finally(() => setLoading(false));
  }, []);

  const replace = (m) =>
    setMemberships((prev) => prev.map((x) => (x.id === m.id ? m : x)));

  const recordRefill = async (id) => {
    setBusy(`refill-${id}`);
    try {
      const res = await api.post(`/admin/memberships/${id}/refill`);
      replace(res.data);
    } finally {
      setBusy(null);
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this membership at the end of its billing period?"))
      return;
    setBusy(`cancel-${id}`);
    try {
      const res = await api.post(`/admin/memberships/${id}/cancel`);
      replace(res.data);
    } finally {
      setBusy(null);
    }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

  if (loading) return <p>Loading…</p>;

  return (
    <div>
      <AdminNav />
      <h1 className="mb-5 text-2xl font-extrabold">Refill Memberships</h1>

      {memberships.length === 0 ? (
        <p className="text-gray-500">No memberships yet.</p>
      ) : (
        <div className="space-y-3">
          {memberships.map((m) => (
            <div key={m.id} className="card flex items-center justify-between p-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold">{m.name}</span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                      m.status === "active"
                        ? "bg-fairway-100 text-fairway-700"
                        : m.status === "cancelled"
                          ? "bg-gray-200 text-gray-600"
                          : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {m.status}
                  </span>
                  {m.cancel_requested && m.status !== "cancelled" && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                      cancellation pending
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600">{m.email}</p>
                <p className="text-sm text-gray-500">
                  Next refill: <strong>{fmt(m.next_refill_date)}</strong> ·{" "}
                  {(m.refills || []).length} shipped ·{" "}
                  {(m.renewals || []).length} renewals
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  className="btn-outline"
                  disabled={busy === `refill-${m.id}`}
                  onClick={() => recordRefill(m.id)}
                >
                  <PackageCheck size={16} />
                  {busy === `refill-${m.id}` ? "Saving…" : "Refill shipped"}
                </button>
                {m.status !== "cancelled" && !m.cancel_requested && (
                  <button
                    className="btn border border-red-300 text-red-600 hover:bg-red-50"
                    disabled={busy === `cancel-${m.id}`}
                    onClick={() => cancel(m.id)}
                  >
                    <XCircle size={16} />
                    {busy === `cancel-${m.id}` ? "…" : "Cancel"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
